import { Button } from '@/components/Buttons';
import { BUTTON_LABELS, FEATURE_FLAGS } from '@/constants';
import {
  useAppLoading,
  useResponsive,
  useSimulationRouting,
} from '@/hooks';
import { cn } from '@/utils/cn';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { SkeletonLoader } from '@/components/Loaders';
import { TextContent } from '@/components/Text';
import { TrainingSimulationVoiceLive } from '@/components/Simulations';
import { useAuth } from '@/components/Auth';
import { useGlobalStore } from '@/store';
import React, { useState, useMemo } from 'react';

/**
 * Component for handling roleplay simulation interface
 */
export const SimulationRoleplay: React.FC = () => {
  // ===============================
  // Hooks and state
  // ===============================
  const isLoading = useAppLoading();
  const isLgUp = useResponsive({ breakpoint: 'lg', direction: 'min' });
  const isXlUp = useResponsive({ breakpoint: 'xl', direction: 'min' });
  const { navigateToDashboard, navigateToSimulation, navigateToSimulationResults } = useSimulationRouting();
  const [isListening, _] = useState(false);
  const { user } = useAuth();
  const {
    activeCertification,
    activePersona,
    activeSessionId,
    activeSimulation,
    setActiveSessionId,
  } = useGlobalStore();

  /**
   * Determine if we should show the loader based on loading state and required data availability
   * We need to ensure we have the active simulation, active persona, and user
   * information before showing the simulation interface
   */
  const showLoader = useMemo(() => {
    return isLoading || !activeSimulation || !activePersona || !user;
  }, [isLoading, activeSimulation, activePersona, user]);

  // ===============================
  // Callbacks & handlers
  // ===============================

  /**
   * Handle exiting the simulation roleplay interface
   */
  const handleExit = () => {
    if (activeCertification && activeSimulation && activeSessionId) {
      const certificationId = activeCertification.id.toString();
      const simulationId = activeSimulation.id.toString();

      navigateToSimulationResults(certificationId, simulationId, {
        sessionId: activeSessionId.toString(),
      });
    } else if (activeCertification && activeSimulation) {
      navigateToSimulation(activeCertification, activeSimulation);
    } else {
      navigateToDashboard();
    }
  };

  /**
   * Callback for when a new session is created
   */
  const handleSessionCreated = (sessionId: number) => {
    console.log('Session created with ID:', sessionId);
    setActiveSessionId(sessionId);
  };

  /**
   * Callback for toggling captions on/off. Currently just logs the action,
   * but can be expanded to actually toggle captions in the UI.
   */
  const handleToggleCaptions = () => {
    console.log('Toggle captions');
  };

  /**
   * Callback for toggling camera on/off. Currently just logs the action,
   * but can be expanded to actually toggle the camera in the UI.
   */
  const handleToggleCamera = () => {
    console.log('Toggle camera');
  };

  /**
   * Callback for pausing the simulation. Currently just logs the action,
   * but can be expanded to actually pause the simulation in the UI.
   */
  const handlePause = () => {
    console.log('Pause simulation');
  };

  /**
   * Callback for restarting the simulation. Currently just logs the action,
   * but can be expanded to actually restart the simulation in the UI.
   */
  const handleRestart = () => {
    console.log('Restart simulation');
  };

  // ================================
  // Sub-components
  // ================================

  /**
   * Header sub-component for displaying certification and simulation
   * titles along with a close button
   */
  const HeaderComponent = ({
    certificationTitle,
    isLoading,
    simulationTitle,
  }: {
    certificationTitle: string;
    isLoading: boolean;
    simulationTitle: string;
  }) => (
    <header className="hidden w-full sm:block">
      <div className="flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-6 md:flex-row">
          <div className="flex-0 flex min-h-8 items-center justify-start">
            <TextContent
              className={cn(
                'whitespace-nowrap',
                isLgUp ? 'typography-section-title' : 'typography-section-subtitle',
              )}
              isLoading={isLoading}
              label={certificationTitle}
              level={1}
              loadingClassName={cn('w-[18rem]', isXlUp ? 'h-[2rem]' : 'h-[1.5rem]')}
              type="headline"
            />
          </div>
          <div className="flex min-h-8 w-full flex-1 items-center justify-between border-semantic-globals-border md:w-auto md:border-l md:pl-6">
            <TextContent
              className={cn(isLgUp ? 'typography-section-title' : 'typography-section-subtitle')}
              isLoading={isLoading}
              label={simulationTitle}
              level={2}
              loadingClassName={cn('w-[24rem]', isXlUp ? 'h-[2rem]' : 'h-[1.5rem]')}
              type="headline"
            />
            <Button
              className="text-neutral-black"
              disabled={isLoading}
              iconLeft="X"
              label={BUTTON_LABELS.CLOSE}
              onClick={handleExit}
              size="small"
              variant="tertiary"
            />
          </div>
        </div>
      </div>
    </header>
  );

  /**
   * IVizSection sub-component that represents a placeholder for IVIZ integration.
   * This section can be replaced with actual IVIZ components and logic when the feature flag is enabled.
   */
  const IVizSection = () => (
    <div className="h-full w-full">
      <embed
        src="/cp-50480v28 - Derm HCP iVis Update.pdf"
        title="Example Site"
        width="100%"
        height="100%"
        // sandbox="allow-scripts allow-same-origin"
        type="application/pdf"
      />
    </div>
  );

  /**
   * SeperatorComponent sub-component that represents a styled separator between the two panels
   * when the IVIZ integration feature flag is enabled.
   */
  const SeperatorComponent = () => (
    <Separator className="rounded-xs flex w-4 items-center justify-center bg-slate-600 text-slate-900 focus:bg-slate-400 sm:w-2 [&[data-separator='active']]:bg-slate-400 [&[data-separator='active']]:text-slate-950 [&[data-separator='disabled']]:opacity-50 [&[data-separator='hover']]:bg-slate-500 [&[data-separator='hover']]:text-slate-950">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 0 24 24"
        width="24px"
        fill="currentColor"
        className="h-6 w-6 shrink-0 sm:h-4 sm:w-4"
      >
        <path d="M9 6H9.01M15 6H15.01M15 12H15.01M9 12H9.01M9 18H9.01M15 18H15.01M10 6C10 6.55228 9.55228 7 9 7C8.44772 7 8 6.55228 8 6C8 5.44772 8.44772 5 9 5C9.55228 5 10 5.44772 10 6ZM16 6C16 6.55228 15.5523 7 15 7C14.4477 7 14 6.55228 14 6C14 5.44772 14.4477 5 15 5C15.5523 5 16 5.44772 16 6ZM10 12C10 12.5523 9.55228 13 9 13C8.44772 13 8 12.5523 8 12C8 11.4477 8.44772 11 9 11C9.55228 11 10 11.4477 10 12ZM16 12C16 12.5523 15.5523 13 15 13C14.4477 13 14 12.5523 14 12C14 11.4477 14.4477 11 15 11C15.5523 11 16 11.4477 16 12ZM10 18C10 18.5523 9.55228 19 9 19C8.44772 19 8 18.5523 8 18C8 17.4477 8.44772 17 9 17C9.55228 17 10 17.4477 10 18ZM16 18C16 18.5523 15.5523 19 15 19C14.4477 19 14 18.5523 14 18C14 17.4477 14.4477 17 15 17C15.5523 17 16 17.4477 16 18Z"></path>
      </svg>
    </Separator>
  );

  // ================================
  // Render
  // ================================
  return (
    <section className="mx-auto flex w-full flex-col gap-16">
      <HeaderComponent
        certificationTitle={activeCertification?.title || ''}
        simulationTitle={activeSimulation?.title || ''}
        isLoading={showLoader}
      />

      {isLoading ? (
        <div className="relative flex h-[65vh] w-full flex-col gap-8">
          <div className="mx-auto h-[426px] w-[448px]">
            <SkeletonLoader className="h-full w-full" rounded="2xl" />
          </div>
          <div className="mx-auto flex w-[629px] flex-col items-center justify-center gap-0.5">
            <SkeletonLoader height="sm" rounded="none" width="full" />
            <SkeletonLoader height="sm" rounded="none" width="2/3" />
          </div>
          <div className="flex items-center justify-center gap-6">
            <SkeletonLoader className="h-[3.5rem] w-[18.5rem]" rounded="3xl" />
            <SkeletonLoader className="h-[3.5rem]" rounded="3xl" width="sm" />
          </div>
        </div>
      ) : (
        <div className="animate-fade-in animate-duration-fast animate-iteration-count-once">
          {FEATURE_FLAGS.ENABLE_IVIS_INTEGRATION ? (
            <Group>
              <Panel defaultSize="50%">
                <IVizSection />
              </Panel>
              <SeperatorComponent />
              <Panel>
                <TrainingSimulationVoiceLive
                  isListening={isListening}
                  onClose={handleExit}
                  onExit={handleExit}
                  onPause={handlePause}
                  onRestart={handleRestart}
                  onSessionCreated={handleSessionCreated}
                  onToggleCamera={handleToggleCamera}
                  onToggleCaptions={handleToggleCaptions}
                  persona={activePersona || undefined}
                  scenarioId={activeSimulation?.id.toString() || undefined}
                  userId={user?.uuid}
                />
              </Panel>
            </Group>
          ) : (
            <TrainingSimulationVoiceLive
              isListening={isListening}
              onClose={handleExit}
              onExit={handleExit}
              onPause={handlePause}
              onRestart={handleRestart}
              onSessionCreated={handleSessionCreated}
              onToggleCamera={handleToggleCamera}
              onToggleCaptions={handleToggleCaptions}
              persona={activePersona || undefined}
              scenarioId={activeSimulation?.id.toString() || undefined}
              userId={user?.uuid}
            />
          )}
        </div>
      )}
    </section>
  );
};

SimulationRoleplay.displayName = 'SimulationRoleplay';

export default SimulationRoleplay;
